import { OperationVariables, TypedDocumentNode, useMutation, useQuery } from '@apollo/client';
import { UseEditableWidgetBackendWithProps } from '@procureai/ui-modules-mui';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { DocumentNode } from 'graphql';
import { useMemo } from 'react';
import { DefaultValues, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ZodType } from 'zod';

interface CreateEditableWidgetGraphqlBackendHookProps<
  FormData extends FieldValues,
  QueryData = any,
  QueryVariables extends OperationVariables = OperationVariables,
  MutationData = any,
  MutationVariables extends OperationVariables = OperationVariables,
> {
  schema: ZodType<FormData>;
  query: DocumentNode | TypedDocumentNode<QueryData, QueryVariables>;
  mutation: DocumentNode | TypedDocumentNode<MutationData, MutationVariables>;
}

export interface SimpleFormWidgetPropsState {
  disabled?: boolean;
}

interface EditableWidgetGraphqlBackendHookProps<
  FormData extends FieldValues,
  QueryData = any,
  QueryVariables extends OperationVariables = OperationVariables,
  MutationVariables extends OperationVariables = OperationVariables,
> {
  variables?: QueryVariables;
  skip?: boolean;
  toFormData: (data: QueryData) => DefaultValues<FormData>;
  toMutationData: (formData: FormData, data: QueryData | undefined) => MutationVariables;
  toSimpleFormWidgetProps?: (data: QueryData | null, loading: boolean) => SimpleFormWidgetPropsState;
}

export const createEditableWidgetGraphqlBackendHook = <
  FormData extends FieldValues,
  QueryData = any,
  QueryVariables extends OperationVariables = OperationVariables,
  MutationData = any,
  MutationVariables extends OperationVariables = OperationVariables,
>({
  query,
  mutation,
}: CreateEditableWidgetGraphqlBackendHookProps<
  FormData,
  QueryData,
  QueryVariables,
  MutationData,
  MutationVariables
>): UseEditableWidgetBackendWithProps<
  FormData,
  EditableWidgetGraphqlBackendHookProps<FormData, QueryData, QueryVariables, MutationVariables>
> => {
  return ({ i18n, variables, skip, toFormData, toMutationData, toSimpleFormWidgetProps }) => {
    const { onError, enqueueSuccessSnackbar } = useCustomSnackbar();
    const { t } = useTranslation(i18n?.ns, { keyPrefix: i18n?.keyPrefix });

    const { data, loading } = useQuery<QueryData, QueryVariables>(query, {
      variables: variables,
      skip: skip,
      fetchPolicy: 'cache-and-network',
    });

    const [update, { loading: saving }] = useMutation<MutationData, MutationVariables>(mutation, {
      onError,
      onCompleted: () => {
        enqueueSuccessSnackbar(t('updateSuccess'));
      },
    });

    const dataMapped = useMemo(() => {
      return data ? toFormData(data) : null;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const save = async (formData: FormData) => {
      if (skip) {
        return { success: false };
      }
      const { errors } = await update({
        variables: toMutationData(formData, data),
      });
      return {
        success: !errors?.length,
      };
    };

    const disabled = useMemo(() => {
      if (toSimpleFormWidgetProps) {
        const props = toSimpleFormWidgetProps(data ?? null, loading);
        return props.disabled ?? loading;
      }
      return loading;
    }, [toSimpleFormWidgetProps, data, loading]);

    return {
      loading,
      saving,
      disabled,
      save,
      data: dataMapped,
    };
  };
};
